/**
 * Update Limits Modal Component - Unified Admin Module
 * 
 * Reusable modal for updating user limits on existing monthly plans.
 * Used in user detail views for consistency.
 */

import React, { useState, useEffect } from 'react';
import { useAdminData } from '../hooks/useAdminData';
import { getPlanInfo } from '../utils';
import type { PlanType, UserIdentifier } from '../types';

interface UpdateLimitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userIdentifier: UserIdentifier;
  currentPlan: PlanType;
  currentWeeklyAllowance?: number;
  currentMonthlyAllowance?: number;
  userDisplayName?: string;
}

/**
 * Modal for updating limits on existing monthly plans
 */
export const UpdateLimitsModal: React.FC<UpdateLimitsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userIdentifier,
  currentPlan,
  currentWeeklyAllowance,
  currentMonthlyAllowance,
  userDisplayName
}) => {
  const { operations, billingConfig } = useAdminData();
  
  // Form state
  const [limitsForm, setLimitsForm] = useState({
    monthlyLimit: ''
  });

  // Load billing configuration when modal opens
  useEffect(() => {
    if (isOpen) {
      billingConfig.loadConfig();
      // Reset form when modal opens
      setLimitsForm({
        monthlyLimit: ''
      });
    }
  }, [isOpen]);

  const handleUpdateLimits = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userIdentifier) return;

    // Parse limits
    let monthlyLimit: number | undefined;

    if (limitsForm.monthlyLimit) {
      monthlyLimit = parseFloat(limitsForm.monthlyLimit);
      if (isNaN(monthlyLimit) || monthlyLimit < 0) {
        alert('Please enter a valid monthly limit');
        return;
      }
    }

    if (monthlyLimit === undefined) {
      alert('Please enter a monthly limit to update');
      return;
    }

    const success = await operations.updateLimits(userIdentifier, undefined, monthlyLimit);

    if (success) {
      setLimitsForm({
        monthlyLimit: ''
      });
      onSuccess();
      onClose();
    }
  };

  const handleClose = () => {
      setLimitsForm({
        monthlyLimit: ''
      });
    operations.clearError();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Update Limits{userDisplayName && ` for ${userDisplayName}`}
          </h3>
          
          <form onSubmit={handleUpdateLimits} className="space-y-4">
            {currentPlan === 'monthly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Limit ($)
                  {billingConfig.config && (
                    <span className="text-xs text-gray-500 ml-1">
                      (Current: ${currentMonthlyAllowance}, Default: ${billingConfig.config.default_monthly_allowance})
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="10000"
                  value={limitsForm.monthlyLimit}
                  onChange={(e) => setLimitsForm(prev => ({ ...prev, monthlyLimit: e.target.value }))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder={currentMonthlyAllowance?.toString() || "50.00"}
                />
              </div>
            )}
            
            <div className="bg-blue-50 p-3 rounded-md">
              <p className="text-sm text-blue-800">
                <strong>Current Plan:</strong> {getPlanInfo(currentPlan).label}
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Leave fields empty to keep current values. Only enter values you want to change.
              </p>
            </div>
            
            {operations.error && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {operations.error}
              </div>
            )}
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={operations.loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {operations.loading ? 'Updating...' : 'Update Limits'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateLimitsModal;
